import type { ExitBadgeType, InvestExitWay } from '../model/exit';
import type { InvestStatusBadgeType } from '../model/invest-status';
import type { InvestType } from '../model/invest-type';
import type { ConversionType, InvestChangeType } from '../model/investment-change';

/** 투자 상태 배지 글자 */
export const INVEST_STATUS_BADGE_LABEL: Record<InvestStatusBadgeType, string> = {
  'complete-exit': '회수',
  'partial-exit': '부분회수',
  'complete-impairment': '감액',
  'partial-impairment': '부분감액',
  shutdown: '폐업',
  'pre-shutdown': '폐업예정',
};

/** 회수 유형 배지 글자(투자 상태 배지와 같은 표) */
export const EXIT_BADGE_LABEL: Record<ExitBadgeType, string> = {
  'complete-exit': INVEST_STATUS_BADGE_LABEL['complete-exit'],
  'partial-exit': INVEST_STATUS_BADGE_LABEL['partial-exit'],
};

/** 코드: InvestTypeOptionLabel */
export const INVEST_TYPE_LABEL: Record<InvestType, string> = {
  RCPS: 'RCPS',
  CPS: 'CPS',
  CB: 'CB',
  ConvertibleNote: 'CN',
  SAFE: 'SAFE',
  BW: 'BW',
  Common: '보통주',
  Preferred: '우선주',
};

/** investType 이 없는 보유 주식 */
export const INVEST_TYPE_MIXED_LABEL = '복합';

/** 코드: InvestChangeTypeLabel */
export const INVEST_CHANGE_TYPE_LABEL: Record<InvestChangeType, string> = {
  BonusIssue: '무상증자',
  StockSplit: '액면분할',
  StockMerge: '합병',
  Conversion: '전환',
  Refix: '리픽싱',
  PartialExit: '부분회수',
  CompleteExit: '완전회수',
  Interest: '이자 납입',
  Shutdown: '폐업',
  PreShutdown: '폐업예정',
  PartialImpairment: '부분감액',
  CompleteImpairment: '감액',
  OvercomeImpairment: '감액취소',
  InvestD3: 'D3투자',
  InvestInternal: 'D3투자',
  InvestExternal: '후속투자',
};

/** 코드: InvestChangeConversionOptionLabel */
export const CONVERSION_TYPE_LABEL: Record<ConversionType, string> = {
  RCPS: 'RCPS',
  CPS: 'CPS',
  Common: '보통주',
  Preferred: '우선주',
};

/** 코드: InvestChangeExitOptionLabel. 빈 글자는 표시하지 않는다 */
export const INVEST_EXIT_WAY_LABEL: Record<InvestExitWay, string> = {
  IPO: 'IPO',
  MnA: 'M&A',
  Secondary: '',
  Redemption: '',
};

/** 줄 · 카드의 고정 글자 */
export const INVESTMENT_TEXT = {
  investD3: 'D3투자',
  current: '현재가치',
  refixed: '실질가치',
  irr: 'IRR',
  multiple: 'multiple',
  valuationAndExit: '평가 및 회수',
  edit: '편집',
  editLabel: (type: string, date: string) => `${type} ${date} 편집`,
  commentLabel: '비고 보기',
  header: {
    type: '유형',
    date: '날짜',
    option: '종류(비율)',
    stock: '주식수',
    price: '주당가치',
    amount: '금액',
    ratio: '지분율',
    comment: '비고',
    action: '편집',
  },
  /** 금액 단위(백만원 · 천달러) */
  millionKrw: '백만원',
  thousandUsd: 'K$',
  shareUnit: '주',
  /** 계산식: ( 12,500주 x 120,000 KRW ) */
  formula: (stock: string, price: string, unit: string) => `( ${stock}주 x ${price} ${unit} )`,
} as const;

export const INVEST_BLOCK_TEXT = {
  addChange: '지분 변동 내역 추가하기',
  addChangeLabel: (fund: string) => `${fund} 지분 변동 내역 추가하기`,
} as const;

export const INVEST_METRIC_TEXT = {
  irr: 'IRR',
  multiple: 'Multiple',
  irrHelp: 'IRR1: 투자기업의 투자, 회수, 미회수자산 평가금액으로 계산한 펀드의 Gross IRR',
  multipleHelp:
    '회수 및 평가총액/누적 투자금액 ( 회수자산은 회수일 기준, 미회수 자산은 분기 말 기준 )',
  helpSuffix: '설명',
} as const;

export const STOCK_CARD_TEXT = {
  initial: '최초 투자 금액',
  initialNote: '* 투자금/주식수/증권종류/지분률',
  valuation: '현재 평가 가치',
  openDetail: '평가 상세',
  fund: '펀드명',
  amount: 'D3 투자 금액',
  stock: '주식수',
  listLabel: (title: string) => `${title} 목록`,
  state: {
    'complete-exit': '회수 완료',
    'partial-exit': '부분 회수',
    shutdown: '폐업',
    impairment: '감액',
    'partial-impairment': '부분 감액',
  },
  shares: (count: string) => `${count}주`,
  sharesWithRatio: (count: string, ratio: string) => `${count}주 (${ratio})`,
  withDate: (text: string, date: string) => `${text} (${date})`,
  dateOnly: (date: string) => `(${date})`,
} as const;
